<template lang="pug">
Toast
Card.card.loan(v-if="availableLoanOpts && availableLoanOpts.length")
  template(#title) {{ $t('loan.title') }}
  template(#content) 
    .loan-type
      Dropdown(
        v-model="selectedLoanValue"
        :options="availableLoanOpts"
        optionLabel="name"
        optionValue="name"
        @change="updateSelectedLoan()"
      )
    .loan-info(v-if="showLoanInfo")
      LoanInfo(:term-min-max="termMinMax" :loan-amount-min-max="loanAmountMinMax")
    Card.loan-result(v-if="showLoanInfo")
      template(#content)
        MortgageSummary(
          v-if="selectedLoanOption.loanType === LoanType.Mortgage"
          :energySavings="currentBerRatingReport?.monthlyEnergySavingsInTerm"
          :mortgageSavings="currentBerRatingReport?.monthlyMortgageSavingsFor250k"
          :monthlyCost="{ apr: report?.loanDetails.apr, rating: report.afterRating.code, cost: report?.loanDetails.estimatedMonthlyCost }"
          :monthlyNetCost="currentBerRatingReport?.monthlyNetCost"
        )
        LoanSummary(
          v-else
          :own-funds="ownFunds"
          :monthlyCost="monthlyCost"
          :repayable="paymentBand?.fixedFee && paymentBand.apr ? report.estimatedTotalCostPlusLoanInterest : undefined"
          :apr="paymentBand?.apr"
          :fixedFee="paymentBand?.fixedFee"
        )
</template>
<script lang="ts">
import { computed, defineComponent } from "vue";
import { ref } from "vue";
import Card from "primevue/card";
import Dropdown from "primevue/dropdown";
import Toast from "primevue/toast";
import LoanInfo from "./LoanInfo.vue";
import LoanSummary from "./LoanSummary.vue";
import MortgageSummary from "./MortgageSummary.vue";
import { useLoan } from "@/store/loan";
import { storeToRefs } from "pinia";
import { useRecommendations } from "@/store/recommendations";
import { useToast } from "primevue/usetoast";
import {
  formatPercentageToString,
  formatNumberToString,
  on,
  BerwowEvent,
  TrackingEvent,
} from "@/helpers";
import { LoanType } from "@/types";
import { DEFAULT_MAX_TERM } from "@/types/constants";
import { useSettings } from "@/store";

export default defineComponent({
  name: "LoanTypeCard",
  components: {
    Card,
    Dropdown,
    LoanInfo,
    Toast,
    LoanSummary,
    MortgageSummary,
  },
  setup() {
    const recommendations = useRecommendations();
    const { report } = storeToRefs(recommendations);
    const { settings } = storeToRefs(useSettings());
    const {
      totalAmount,
      maxAmount,
      availableLoanOpts,
      selectedLoanOption,
      paymentBand,
      loanAmount,
    } = storeToRefs(useLoan());
    const { setSelectedLoanOption } = useLoan();

    const toast = useToast();
    const showToast = () => {
      toast.add({
        severity: "info",
        summary: "Loan type updated",
        detail: "The NET project cost amount is unavailable for the selected loan option",
        life: 5000,
      });
    };

    //LOAN
    const selectedLoanValue = ref(selectedLoanOption.value?.name);
    const selectedLoan = computed(() =>
      availableLoanOpts.value.find((loan) => selectedLoanValue.value === loan.name)
    );

    on(BerwowEvent.UnavailableAmount, () => {
      selectedLoanValue.value = "No loan";
      showToast();
    });

    async function updateSelectedLoan() {
      if (selectedLoan.value) {
        await setSelectedLoanOption(selectedLoan.value);
        recommendations.emitRecommendationsReportResult(
          false,
          TrackingEvent.UserChangedLoan
        );
      }
    }

    const min = computed(() =>
      selectedLoan.value && selectedLoan.value.minAmount
        ? selectedLoan.value.minAmount
        : selectedLoan.value && selectedLoan.value.paymentBands
          ? selectedLoan.value?.paymentBands[0].minAmount
          : 0
    );

    const max = computed(() => {
      if (!selectedLoan.value?.paymentBands && !selectedLoan.value?.maxAmount) return;
      const paymentBands = selectedLoan.value.paymentBands;
      const maxAmount = paymentBands
        ? paymentBands[paymentBands.length - 1].maxAmount
        : selectedLoan.value?.maxAmount
          ? selectedLoan.value?.maxAmount
          : 0;
      return totalAmount.value > maxAmount ? maxAmount : totalAmount.value;
    });

    //AMOUNT
    const loanAmountMinMax = computed(() => [min.value, max.value]);

    //TERM
    const termMinMax = computed(() =>
      paymentBand.value
        ? [paymentBand.value.minTerm, paymentBand.value.maxTerm]
        : settings.value?.defaultTerm ? [settings.value.defaultTerm.min, settings.value.defaultTerm.max] : [1, DEFAULT_MAX_TERM]
    );

    const showLoanInfo = computed(
      () =>
        selectedLoanOption.value != null &&
        ((selectedLoan.value?.paymentBands && selectedLoan.value?.paymentBands.length) ||
          selectedLoanOption.value.loanType === LoanType.Mortgage) &&
        totalAmount.value !== -1
    );

    //RESULT
    const ownFunds = computed(() => {
      return maxAmount.value
        ? maxAmount.value - loanAmount.value
        : 0;
    });
    const monthlyCost = computed(() => report.value?.loanDetails?.estimatedMonthlyCost);
    const currentBerRatingReport = computed(() => report.value?.currentBerRatingReport);

    return {
      selectedLoanValue,
      selectedLoanOption,
      selectedLoan,
      availableLoanOpts,
      loanAmountMinMax,
      termMinMax,
      totalAmount,
      showLoanInfo,
      ownFunds,
      monthlyCost,
      updateSelectedLoan,
      report,
      loanAmount,
      formatNumberToString,
      formatPercentageToString,
      paymentBand,
      LoanType,
      currentBerRatingReport,
    };
  },
});
</script>
<style scoped lang="scss">
.p-dropdown {
  width: 100%;
}

.loan-info {
  margin: 1rem 0;
}

.loan-result {
  margin-top: 1.75rem;

  :deep(.p-card-body) {
    height: 100%;

    .p-card-content {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      gap: 1rem;
    }
  }
}

@media screen and (max-width: 1120px) and (min-width: 725px) {
  .loan {
    :deep(> .p-card-body) {
      >.p-card-content {
        display: grid;
        grid-template: 5rem / 23rem auto 35rem;
        row-gap: 3rem;

        .loan-info {
          grid-row: 2/3;
          grid-column: 1/3;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
        }

        .loan-result {
          grid-row: 2/3;
          grid-column: 3/4;
          margin-left: 2rem;
        }
      }
    }
  }
}

@media screen and (max-width: 1120px) and (min-width: 725px) {
  .loan {
    :deep(> .p-card-body) {
      >.p-card-content {
        grid-template: 3rem / 20rem auto 20rem;
        row-gap: 5rem;
      }
    }
  }
}
</style>
